import { useEffect, useMemo } from 'react';
import {
  KilldateCountdownContainer,
  NotificationMessageContainer,
} from 'viewDeprecation/atoms';
import {
  DeprecatedSlidesNotificationProperties,
  OverallPresentationViewInfo,
  PresentationSlideViewInfo,
} from 'viewDeprecation/types';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import { getSlideMigrationInfo } from 'presentation/actions';
import { Box, Stack } from '@ardoq/layout';
import {
  DeprecatedSlideNotificationDetails,
  SolutionAdvice,
} from './DeprecatedSlidesNotificationParts';
import {
  currentDate,
  formatDuration,
  intervalToDuration,
} from '@ardoq/date-time';
import { LoaderSize, LoadingOverlay } from '@ardoq/loader';
import { APIPresentationAssetAttributes } from '@ardoq/api-types';
import { ErrorNotification, WarningNotification } from '@ardoq/status-ui';
import { CanCreatorMigrateSlidesInfo } from '@ardoq/api';
import {
  PermissionContext,
  permissionsOperations,
  presentationPermissionOperations,
} from '@ardoq/access-control';
import { deprecatedSlides$ } from 'viewDeprecation/deprecatedSlides$';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { getPhaseMessageText } from 'viewDeprecation/DepreciatedSlideNotificationUtil';

export const expressTimespan = (start: Date, end: Date) =>
  formatDuration(intervalToDuration({ end, start }), {
    format: ['years', 'months', 'weeks', 'days'],
  });

type DeprecatedSlidesNotificationViewProps =
  DeprecatedSlidesNotificationProperties & WithI18nLocale;

/**
 * A notification in the presentation sidebar, that summarizes deprecation process info
 * or all slides containing a deprecated view within the current presentation.
 *
 * @see https://ardoqcom.atlassian.net/wiki/spaces/INSIGHT/pages/2413002789/Discontinued+Views+engineering+perspective
 */
const DeprecatedSlidesNotificationBase = ({
  canEditPresentation,
  slidesInfo,
  overallPresentationViewInfo,
  deprecatedSlides,
  minimumKillDate,
  permissionContext,
  creatorCanMigrateNothing,
  creatorCanMigrateSome,
  discontinuedSlidesPresent,
  presentation,
  shouldShowNotification,
  i18nLocale,
}: DeprecatedSlidesNotificationViewProps) => {
  const needsUpdate = useMemo(
    () =>
      !overallPresentationViewInfo ||
      (presentation &&
        overallPresentationViewInfo.presentation._id !== presentation._id),
    [overallPresentationViewInfo, presentation]
  );

  useEffect(() => {
    if (needsUpdate) {
      dispatchAction(getSlideMigrationInfo(presentation));
    }
  }, [overallPresentationViewInfo, presentation, needsUpdate]);

  if (
    !(
      permissionContext &&
      overallPresentationViewInfo &&
      shouldShowNotification
    )
  ) {
    return null;
  }

  const isLoading = !presentation || needsUpdate;

  if (isLoading) {
    return <LoadingOverlay loaderSize={LoaderSize.MEDIUM} />;
  }

  const phaseMessageText = getPhaseMessageText({
    overallPresentationViewInfo,
    isKilldated: !!minimumKillDate,
    i18nLocale,
  });

  const body = (
    <DeprecatedNotificationBody
      minimumKillDate={minimumKillDate}
      permissionContext={permissionContext}
      activePresentation={presentation}
      overallPresentationViewInfo={overallPresentationViewInfo}
      creatorCanMigrateNothing={creatorCanMigrateNothing}
      creatorCanMigrateSome={creatorCanMigrateSome}
      canEditPresentation={canEditPresentation}
      showExtraDetails={!creatorCanMigrateNothing}
      deprecatedSlides={deprecatedSlides}
      phaseMessageText={phaseMessageText}
      slidesInfo={slidesInfo}
      i18nLocale={i18nLocale}
    />
  );

  return (
    <Box backgroundColor="bgDefault" paddingY="small" paddingX="medium">
      {discontinuedSlidesPresent ? (
        <ErrorNotification>{body}</ErrorNotification>
      ) : (
        <WarningNotification>{body}</WarningNotification>
      )}
    </Box>
  );
};

type DeprecatedNotificationBodyProps = {
  minimumKillDate: Date | null;
  permissionContext: PermissionContext;
  activePresentation: APIPresentationAssetAttributes;
  creatorCanMigrateNothing: boolean;
  creatorCanMigrateSome: boolean;
  canEditPresentation: boolean;
  showExtraDetails: boolean;
  deprecatedSlides: PresentationSlideViewInfo[];
  overallPresentationViewInfo: OverallPresentationViewInfo;
  phaseMessageText?: string;
  slidesInfo?: CanCreatorMigrateSlidesInfo;
  i18nLocale: I18nLocale;
};

const DeprecatedNotificationBody = ({
  minimumKillDate,
  permissionContext,
  activePresentation,
  overallPresentationViewInfo,
  creatorCanMigrateNothing,
  creatorCanMigrateSome,
  canEditPresentation,
  showExtraDetails,
  deprecatedSlides,
  phaseMessageText,
  slidesInfo,
  i18nLocale,
}: DeprecatedNotificationBodyProps) => {
  const now = currentDate();
  const isOrgAdmin = permissionsOperations.hasAdminRole(permissionContext);

  return (
    <>
      {minimumKillDate && minimumKillDate > now ? (
        <div>
          <KilldateCountdownContainer>
            {t(
              i18nLocale,
              'viewDeprecation:notification.removeInTimespan',
              { timeSpan: expressTimespan(now, minimumKillDate) },
              'This presentation contains views that will be removed in {{timeSpan}}.'
            )}
          </KilldateCountdownContainer>
        </div>
      ) : null}
      {phaseMessageText ? (
        <NotificationMessageContainer>
          {phaseMessageText}
        </NotificationMessageContainer>
      ) : null}
      <SolutionAdvice
        isCurrentUserCreator={presentationPermissionOperations.isPresentationOwner(
          permissionContext,
          activePresentation
        )}
        overallPresentationViewInfo={overallPresentationViewInfo}
        creatorCanMigrateNothing={creatorCanMigrateNothing}
        creatorCanMigrateSome={creatorCanMigrateSome}
        i18nLocale={i18nLocale}
      />
      {showExtraDetails && (
        <Box>
          <Stack gap="small">
            {t(
              i18nLocale,
              'viewDeprecation:notification.contactDetails',
              'Contact Details'
            )}
            <DeprecatedSlideNotificationDetails
              deprecatedSlides={deprecatedSlides}
              canEditPresentation={canEditPresentation}
              overallPresentationViewInfo={overallPresentationViewInfo}
              slidesMigrationInfo={slidesInfo}
              isCurrentUserOrgAdmin={isOrgAdmin}
              i18nLocale={i18nLocale}
            />
          </Stack>
        </Box>
      )}
    </>
  );
};

export const DeprecatedSlidesNotification = connect(
  DeprecatedSlidesNotificationBase,
  combineLatest([deprecatedSlides$, i18nLocale$]).pipe(
    map(([state, i18nLocale]) => ({ ...state, i18nLocale }))
  )
);
